import React, { FC, useState, useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { clone, uniqueId } from 'lodash'
import { useAlert } from 'react-alert'
import Tips from '../../../components/common/Tips'
import LoadingSquare from '../../../components/common/LoadingSquare'
import DateModule from '../../../components/task/MonthCalendarModule'
import { dateManipulateDay, endOfMonth, format, nowDate, startOfMonth } from '../../../../utils/date'
import { Link, useHistory } from 'react-router-dom'
import {
  createTaskApprovalApplication,
  fetchTaskApprovalApplication,
  fetchTaskApprovalApplicationDetail,
  fetchTaskApprovalApplicationList,
} from '../../../../states/containers/task/taskSlice'
import Holidays from 'date-holidays'
import { getGetPrams, setPath, setUrlId } from '../../../../utils/url'
import { URL } from '../../../constants/url'
import ApprovedModal from '../../../components/task/ApprovedModal'
import { labelColor, statusText } from '../../../../utils/taskStatusLabel'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import VideoGuide from '../../../components/common/VideoGuide'

interface DetailParam {
  member_id: number
  member_name: string
  target_date: string
}

interface UpdateParam {
  member_id: number
  status: string
  comment: string
  target_date: string
}

interface GetParams {
  date: string
}

const Index: FC = () => {
  const dispatch = useDispatch()
  const alert = useAlert()
  const history = useHistory()

  const {
    task,
    current,
    login,
  } = useSelector((state: RootState) => state)

  const holidays = new Holidays()
  holidays.init('JP')

  const [date, setDate] = useState<string>("")
  const [isOpenDetailModal, setIsOpenDetailModal] = useState<boolean>(false)
  const [wait, setWait] = useState<boolean>(false)

  const [detailData, setDetailData] = useState<DetailParam>({
    member_id: 0,
    member_name: "",
    target_date: "",
  })

  const members = task.taskApprovalApplicationList?.members ?? []

  const targetMonthData = useMemo(() => {
    const dateList = []
    for (let d = clone(new Date(startOfMonth(date || nowDate())));d <= new Date(endOfMonth(date || nowDate()));d.setDate(d.getDate() + 1)) {
      dateList.push(format(d, "YYYY-MM-DD"))
    }
    return Array.from(new Set(dateList))
  }, [date])

  const calendarColor = (day: string, bg = false) => {
    if (Number.isNaN(new Date(day).getTime())) return bg ? "#FFFFFF" : "#666666"
    const holiday = holidays.isHoliday(new Date(day))
    if ((holiday && holiday[0].type === "public") || format(day, 'd') === '0') {
      return bg ? "#fff7f7" : "#FF0000"
    }
    if (format(day, 'd') === '6') {
      return bg ? "#f1f9ff" : "#317AB7"
    }
    return bg ? "#FFFFFF" : "#666666"
  }

  const clickDetail = (memberData: DetailParam) => {
    setDetailData(memberData)
    setIsOpenDetailModal(true)
    dispatch(
      fetchTaskApprovalApplicationDetail(
        {
          id: memberData.member_id.toString(),
          target_date: memberData.target_date,
        },
      ),
    )
  }

  const buildStatus = (memberId: number, targetDate: string) => {
    const memberData = task.taskApprovalApplicationList?.members.find((v) => v.member_id === memberId)
    const detail = memberData?.date_list.find((v) => v.date === targetDate)
    let tmpWord = { text: "", short: "" }
    if (!memberData || !detail || Number(format(targetDate, "YYYYMMDD")) <= 20240430) {
      return (
        <>
          -
        </>
      )
    }
    if (detail.approval_status === "approved") {
      tmpWord = { text: "承認済み", short: "承" }
    } else if (detail.approval_status === "completed") {
      tmpWord = { text: "承認完了", short: "完" }
    } else if (detail.approval_status === "processing") {
      tmpWord = { text: "申請中", short: "申" }
    } else if (detail.approval_status === "skipped") {
      tmpWord = { text: "スキップ", short: "ス" }
    } else if (detail.approval_status === "unapplied" || detail.approval_status === "canceled") {
      tmpWord = { text: "未申請", short: "未" }
    } else if (detail.approval_status === "returned") {
      tmpWord = { text: "差戻し", short: "差" }
    } else if (detail.approval_status === "dropped") {
      tmpWord = { text: "承認取消", short: "消" }
    }
    return (
      <>
        <Tips
          description={tmpWord.text}
          className=""
          icon={false}
        >
          <div
            className="status_icon"
            style={{
              color: labelColor(detail.approval_status),
              border: `solid 1px ${labelColor(detail.approval_status)}`,
              backgroundColor: labelColor(detail.approval_status, true),
              cursor: "pointer",
            }}
            onClick={() => clickDetail({
              member_id: memberData.member_id,
              member_name: memberData.name,
              target_date: targetDate,
            })}
            role="none"
          >
            {tmpWord.short}
          </div>
        </Tips>
      </>
    )
  }

  const buildStatusLegend = (status: string) => {
    let tmpWord = { text: "", short: "" }
    if (status === "approved") {
      tmpWord = { text: "あなたによってデータが承認されました", short: "承" }
    }
    if (status === "completed") {
      tmpWord = { text: "最終承認者によってデータが承認されました", short: "完" }
    }
    if (status === "processing") {
      tmpWord = { text: "データが申請中です", short: "申" }
    }
    if (status === "unapplied") {
      tmpWord = { text: "データが申請されていません", short: "未" }
    }
    if (status === "returned") {
      tmpWord = { text: "データの申請が差戻しされました", short: "差" }
    }
    if (status === "dropped") {
      tmpWord = { text: "データの承認が取り消されました", short: "消" }
    }
    return (
      <>
        <div className="legend_block">
          <div
            className="status_icon"
            style={{
              color: labelColor(status),
              border: `solid 1px ${labelColor(status)}`,
              backgroundColor: labelColor(status, true),
            }}
          >
            {tmpWord.short}
          </div>
          {`${tmpWord.text}`}
        </div>
      </>
    )
  }

  const updateApproval = (param: UpdateParam, callback: VoidFunction) => {
    const tmpParam = {
      status: param.status,
      targets: [
        {
          target_date: param.target_date,
          member_id: param.member_id,
          comment: param.comment,
        },
      ],
    }
    const isComplete = Boolean(param.status === "approved"
      && task.taskApprovalApplicationDetail?.approvers.filter((v) => v.member_id === login.current_member_id && !v.priority).length)
    const memberName = task.taskApprovalApplicationList?.members.find((v) => v.member_id === param.member_id)?.name || "???"
    dispatch(
      createTaskApprovalApplication(
        tmpParam,
        isComplete,
        () => {
          callback()
          alert.success(`${memberName}さん ${format(param.target_date, "YYYY年M月D日")}の申請データを「${statusText(param.status)}」に変更しました`)
          dispatch(
            fetchTaskApprovalApplicationDetail(
              {
                id: param.member_id.toString(),
                target_date: param.target_date,
              },
            ),
          )
        },
      ),
    )
  }

  const reload = () => {
    setWait(true)
    dispatch(
      fetchTaskApprovalApplication({
        start_date: startOfMonth(date, "YYYY-MM-DD"),
        end_date: endOfMonth(date, "YYYY-MM-DD"),
      }),
    )
    setTimeout(() => {
      setWait(false)
    }, 3000)
  }

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    document.querySelector('html')?.classList.add('scroll_contents')
  }, [])

  useEffect(() => {
    const getParams = getGetPrams(location.search) as GetParams
    if (getParams.date) {
      setDate(format(getParams.date, "YYYY-MM-DD"))
      return
    }
    setDate(format(current.targetDate, "YYYY-MM-DD") || nowDate("YYYY-MM-DD"))
  }, [])

  useEffect(() => {
    if (!date) return
    dispatch(
      fetchTaskApprovalApplicationList({
        start_date: startOfMonth(date, "YYYY-MM-DD"),
        end_date: endOfMonth(date, "YYYY-MM-DD"),
      }),
    )
  }, [date])

  return (
    <section className="task_page">
      <Helmet>
        <title>Qasee - 工数管理 - 承認状況</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        承認状況
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          承認状況
          <VideoGuide
            videoId="QLw-8P-S__U"
          />
        </h1>
        <div className="tools">
          <DateModule
            date={date}
            startMonth="202405"
            changeCallback={(callback_date) => {
              setDate(callback_date)
              history.push(`${location.pathname}?date=${callback_date}`)
            }}
          />
        </div>
      </section>
      <section className="page_description">
        <p>
          あなたが承認するメンバーの申請状況を確認することができます。
        </p>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-header">
            <div className="title">
              承認状況
              {`【${format(date, "YYYY年M月")}】`}
              <div className="button_area">
                <button
                  type="button"
                  className="reload"
                  onClick={reload}
                  disabled={wait}
                >
                  <Tips
                    description="最新の情報に更新"
                    className=""
                    icon={false}
                  >
                    <>
                      <FontAwesomeIcon icon={['fas', 'sync-alt']} />
                    </>
                  </Tips>
                </button>
              </div>
            </div>
            <div className="application_legend">
              <div className="legend_row">
                {buildStatusLegend("unapplied")}
                {buildStatusLegend("returned")}
                {buildStatusLegend("dropped")}
              </div>
              <div className="legend_row">
                {buildStatusLegend("processing")}
                {buildStatusLegend("approved")}
                {buildStatusLegend("completed")}
              </div>
            </div>
          </div>
          <div className="block-detail">
            {
              task.isLoading ? (
                <LoadingSquare>
                  <>
                    承認情報を取得しています…
                  </>
                </LoadingSquare>
              ) : (
                <>
                  {
                    !members.length ? (
                      <div
                        style={{
                          textAlign: "center",
                          fontSize: "20px",
                          fontWeight: "bold",
                          color: "#999999",
                          margin: "16px",
                        }}
                      >
                        表示できるデータがありません
                      </div>
                    ) : (
                      <>
                        <div className="calendar_table_row header_area">
                          <div className="left_area">
                            メンバー名
                          </div>
                          <div className="right_area">
                            {
                              targetMonthData.map((v) => (
                                <div
                                  key={`list_${v}`}
                                  style={{
                                    color: calendarColor(v),
                                    width: `calc(100% / ${targetMonthData.length})`,
                                    minWidth: "20px",
                                  }}
                                >
                                  {format(v, "D")}
                                </div>
                              ))
                            }
                          </div>
                        </div>
                        {
                          task.taskApprovalApplicationList?.members.map((member) => (
                            <div
                              key={`row_${member.member_id}`}
                              className="calendar_table_row"
                            >
                              <div className="left_area">
                                <Link
                                  to={`${setPath(setUrlId(URL.TASK_APPROVAL_STATUS_DETAIL.path, 'id', member.member_id), "ja")}?date=${date}`}
                                  style={{ textDecoration: "underline" }}
                                >
                                  {member.name}
                                </Link>
                              </div>
                              <div className="right_area">
                                {
                                  targetMonthData.map((v) => (
                                    <div
                                      key={uniqueId("member_column_")}
                                      style={{
                                        width: `calc(100% / ${targetMonthData.length})`,
                                        minWidth: "20px",
                                        // eslint-disable-next-line max-len
                                        backgroundColor: Number(format(v, "YYYYMMDD")) <= Number(nowDate("YYYYMMDD")) ? calendarColor(v, true) : "#EEEEEE",
                                      }}
                                    >
                                      {buildStatus(member.member_id, v)}
                                    </div>
                                  ))
                                }
                              </div>
                            </div>
                          ))
                        }
                      </>
                    )
                  }
                </>
              )
            }
          </div>
        </div>
      </section>
      <ApprovedModal
        detail={detailData}
        closeCallback={() => setIsOpenDetailModal(false)}
        updateCallback={(param, callback) => updateApproval(param, callback)}
        isDisplay={isOpenDetailModal}
        moveDateCallback={(addNum) => clickDetail({ ...detailData, target_date: dateManipulateDay(detailData.target_date, addNum, "YYYY-MM-DD") })}
        isDisplayWorkTime
      />
    </section>
  )
}

export default Index
